import {
  Autocomplete,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  formatReviewReason,
  TARGETED_REVIEW_REASON_LABEL_KEYS,
  TARGETED_REVIEW_REASONS,
} from "components/manualReview/utils/reviewReasons";
import { useTenantTranslation } from "hooks/formatters";
import { ReviewReason } from "store/api/generatedApi";
import { unlabellableMaterials } from "./modelDashboardHelpers";

interface FlagRulesEditorProps {
  reasons: readonly ReviewReason[];
  onReasonsChange: (reasons: ReviewReason[]) => void;
  quantileInput: string;
  onQuantileInputChange: (value: string) => void;
  quantileInvalid: boolean;
  confusableMaterials: readonly string[];
  onConfusableMaterialsChange: (materials: string[]) => void;
  neverPlannedMaterials: readonly string[];
  onNeverPlannedMaterialsChange: (materials: string[]) => void;
  labellingNameByMaterial: ReadonlyMap<string, string> | undefined;
}

const MaterialsInput = ({
  label,
  materials,
  labellingNameByMaterial,
  onChange,
}: {
  label: string;
  materials: readonly string[];
  labellingNameByMaterial: ReadonlyMap<string, string> | undefined;
  onChange: (materials: string[]) => void;
}) => {
  const { t } = useTenantTranslation();
  const unlabellable = unlabellableMaterials({
    materials,
    labellingNameByMaterial,
  });
  return (
    <Autocomplete
      multiple
      filterSelectedOptions
      size="small"
      options={[
        ...new Set([...(labellingNameByMaterial?.keys() ?? []), ...materials]),
      ]}
      getOptionLabel={(material) =>
        labellingNameByMaterial?.get(material) ?? material
      }
      value={[...materials]}
      onChange={(_, value) => {
        onChange(value);
      }}
      renderValue={(value: readonly string[], getItemProps) =>
        value.map((material, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return (
            <Chip
              key={key}
              label={labellingNameByMaterial?.get(material) ?? material}
              color={unlabellable.includes(material) ? "error" : "default"}
              size="small"
              {...itemProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={unlabellable.length > 0}
          helperText={
            unlabellable.length > 0 ? t("removeUnlabellableMaterials") : ""
          }
          slotProps={{ inputLabel: { shrink: true } }}
        />
      )}
    />
  );
};

export const FlagRulesEditor = ({
  reasons,
  onReasonsChange,
  quantileInput,
  onQuantileInputChange,
  quantileInvalid,
  confusableMaterials,
  onConfusableMaterialsChange,
  neverPlannedMaterials,
  onNeverPlannedMaterialsChange,
  labellingNameByMaterial,
}: FlagRulesEditorProps) => {
  const { t } = useTenantTranslation();

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">{t("flagRules")}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t("flagRulesHelp")}
      </Typography>
      <FormGroup>
        {TARGETED_REVIEW_REASONS.map((reason) => {
          const labelKey = TARGETED_REVIEW_REASON_LABEL_KEYS[reason];
          return (
            <FormControlLabel
              key={reason}
              control={
                <Checkbox
                  size="small"
                  checked={reasons.includes(reason)}
                  onChange={(_, checked) => {
                    onReasonsChange(
                      checked
                        ? [...reasons, reason]
                        : reasons.filter((enabled) => enabled !== reason)
                    );
                  }}
                />
              }
              label={labelKey ? t(labelKey) : formatReviewReason(reason)}
            />
          );
        })}
      </FormGroup>
      <TextField
        label={t("massOutlierQuantile")}
        value={quantileInput}
        onChange={(e) => {
          onQuantileInputChange(e.target.value);
        }}
        type="number"
        size="small"
        fullWidth
        error={quantileInvalid}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { step: "any", min: 0, max: 0.5 },
        }}
        helperText={quantileInvalid ? t("invalidQuantileValue") : ""}
      />
      <MaterialsInput
        label={t("confusableMaterials")}
        materials={confusableMaterials}
        labellingNameByMaterial={labellingNameByMaterial}
        onChange={onConfusableMaterialsChange}
      />
      <MaterialsInput
        label={t("neverPlannedMaterials")}
        materials={neverPlannedMaterials}
        labellingNameByMaterial={labellingNameByMaterial}
        onChange={onNeverPlannedMaterialsChange}
      />
    </Stack>
  );
};
